import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWithAuthRetry, handleApiResponse } from './apiUtils';
import type { AllowedPageSize } from '../components/table/ListPagination';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface ImageLibraryItem {
  id: string;
  externalAccountId: number | null;
  storageUuid: string;
  status: 'pending_upload' | 'active';
  s3Key: string;
  storagePrefix: string;
  migratedFromS3Key: string | null;
  publicUrl: string;
  originalFilename: string;
  contentType: string;
  fileSizeBytes: number | null;
  altText: string;
  label: string | null;
  lastExportedAt: string | null;
  createdAt: string;
  updatedAt: string;
  usedInCount?: number;
  usedIn?: ImageUsageReference[];
}

export interface ImageLibraryUsage {
  usedInCount: number;
  usedIn: ImageUsageReference[];
}

export interface ImageUsageReference {
  type: 'lesson' | 'course';
  id: string;
  title: string;
  /** Owning scope of the usage: null = Emtrain (base) content, else the account id. */
  externalAccountId: number | null;
  /** Resolved account name for account-scoped usages; null for Emtrain usages. */
  accountName: string | null;
}

export interface ImageLibraryListResponse {
  images: ImageLibraryItem[];
  totalCount: number;
  page: number;
  pageSize: AllowedPageSize;
}

export interface CreateImageLibraryItemPayload {
  externalAccountId?: number | null;
  s3Key: string;
  storagePrefix: string;
  publicUrl: string;
  originalFilename: string;
  contentType: string;
  fileSizeBytes?: number | null;
  altText: string;
  label?: string | null;
}

export interface PrepareImageLibraryUploadPayload {
  externalAccountId?: number | null;
  originalFilename: string;
  contentType: string;
  altText: string;
  label?: string | null;
}

export interface CompleteImageLibraryUploadPayload {
  s3Key: string;
  publicUrl: string;
  fileSizeBytes?: number | null;
}

export interface UpdateImageLibraryItemPayload {
  altText?: string;
  label?: string | null;
}

export const IMAGE_LIBRARY_STORAGE_PREFIX = 'courses/image-library';
export const DEFAULT_IMAGE_LIBRARY_PAGE = 1;
export const DEFAULT_IMAGE_LIBRARY_PAGE_SIZE: AllowedPageSize = 25;

export const imageLibraryKeys = {
  all: ['image-library'] as const,
  lists: () => [...imageLibraryKeys.all, 'list'] as const,
  list: (externalAccountId: number | null, query: string, page = DEFAULT_IMAGE_LIBRARY_PAGE, pageSize: AllowedPageSize = DEFAULT_IMAGE_LIBRARY_PAGE_SIZE) =>
    [...imageLibraryKeys.lists(), externalAccountId, query, page, pageSize] as const,
  usage: (id: string | null, scope: number | null) => [...imageLibraryKeys.all, 'usage', scope, id] as const,
  detail: (id: string) => [...imageLibraryKeys.all, 'detail', id] as const,
};

export async function listImageLibraryItems(options: {
  externalAccountId?: number | null;
  query?: string;
  page?: number;
  pageSize?: AllowedPageSize;
  signal?: AbortSignal;
}): Promise<ImageLibraryListResponse> {
  const params = new URLSearchParams();
  if (options.externalAccountId !== undefined) {
    params.set('externalAccountId', options.externalAccountId === null ? 'null' : String(options.externalAccountId));
  }
  if (options.query?.trim()) params.set('q', options.query.trim());
  params.set('page', String(options.page ?? DEFAULT_IMAGE_LIBRARY_PAGE));
  params.set('pageSize', String(options.pageSize ?? DEFAULT_IMAGE_LIBRARY_PAGE_SIZE));

  // { json: 'withBody' } : “only add the Content-Type: application/json header when the request has a body.”
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/image-library?${params.toString()}`, {
    signal: options.signal,
  }, { json: 'withBody' });
  return handleApiResponse<ImageLibraryListResponse>(response, 'Failed to load image library');
}

export async function getImageLibraryItem(id: string): Promise<ImageLibraryItem> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/image-library/${id}`, undefined, { json: 'withBody' });
  return handleApiResponse<ImageLibraryItem>(response, 'Failed to load image');
}

export async function getImageLibraryUsage(id: string, signal?: AbortSignal): Promise<ImageLibraryUsage> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/image-library/${id}/usage`, { signal }, { json: 'withBody' });
  return handleApiResponse<ImageLibraryUsage>(response, 'Failed to load image usage');
}

export function useImageLibraryUsageQuery(id: string | null, scope: number | null) {
  return useQuery({
    queryKey: imageLibraryKeys.usage(id, scope),
    queryFn: ({ signal }) => getImageLibraryUsage(id!, signal),
    enabled: Boolean(id),
    placeholderData: undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
}

export async function createImageLibraryItem(payload: CreateImageLibraryItemPayload): Promise<ImageLibraryItem> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/image-library`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }, { json: 'withBody' });
  return handleApiResponse<ImageLibraryItem>(response, 'Failed to save image to library');
}

export async function prepareImageLibraryUpload(payload: PrepareImageLibraryUploadPayload): Promise<ImageLibraryItem> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/image-library/prepare-upload`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }, { json: 'withBody' });
  return handleApiResponse<ImageLibraryItem>(response, 'Failed to prepare image library upload');
}

export async function completeImageLibraryUpload(
  id: string,
  payload: CompleteImageLibraryUploadPayload
): Promise<ImageLibraryItem> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/image-library/${id}/complete-upload`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, { json: 'withBody' });
  return handleApiResponse<ImageLibraryItem>(response, 'Failed to complete image library upload');
}

export async function updateImageLibraryItem(
  id: string,
  payload: UpdateImageLibraryItemPayload,
  options?: { includeUsage?: boolean }
): Promise<ImageLibraryItem> {
  const suffix = options?.includeUsage === false ? '?includeUsage=false' : '';
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/image-library/${id}${suffix}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, { json: 'withBody' });
  return handleApiResponse<ImageLibraryItem>(response, 'Failed to update image');
}

export async function deleteImageLibraryItem(id: string): Promise<void> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/image-library/${id}`, {
    method: 'DELETE',
  }, { json: 'withBody' });
  if (!response.ok) {
    try {
      await handleApiResponse<never>(response, 'Failed to delete image');
    } catch (error) {
      throw Object.assign(error instanceof Error ? error : new Error('Failed to delete image'), { status: response.status });
    }
  }
}

export async function markImageLibraryItemsExported(publicUrls: string[]): Promise<{ updatedCount: number }> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/image-library/mark-exported`, {
    method: 'POST',
    body: JSON.stringify({ publicUrls }),
  }, { json: 'withBody' });
  return handleApiResponse<{ updatedCount: number }>(response, 'Failed to mark image library assets exported');
}

export function useImageLibraryQuery(options: {
  externalAccountId?: number | null;
  query?: string;
  page?: number;
  pageSize?: AllowedPageSize;
  enabled?: boolean;
}) {
  const externalAccountId = options.externalAccountId ?? null;
  const query = options.query?.trim() ?? '';
  const page = options.page ?? DEFAULT_IMAGE_LIBRARY_PAGE;
  const pageSize = options.pageSize ?? DEFAULT_IMAGE_LIBRARY_PAGE_SIZE;
  return useQuery({
    queryKey: imageLibraryKeys.list(externalAccountId, query, page, pageSize),
    queryFn: ({ signal }) => listImageLibraryItems({ externalAccountId, query, page, pageSize, signal }),
    enabled: options.enabled ?? true,
  });
}

export function useImageLibraryItemQuery(id: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: id ? imageLibraryKeys.detail(id) : imageLibraryKeys.detail(''),
    queryFn: () => getImageLibraryItem(id || ''),
    enabled: Boolean(id) && (options?.enabled ?? true),
  });
}

export function useCreateImageLibraryItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createImageLibraryItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imageLibraryKeys.lists() });
    },
  });
}

export function useUpdateImageLibraryItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateImageLibraryItemPayload }) =>
      updateImageLibraryItem(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: imageLibraryKeys.lists() });
      queryClient.setQueryData(imageLibraryKeys.detail(data.id), data);
    },
  });
}

export function useDeleteImageLibraryItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteImageLibraryItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imageLibraryKeys.lists() });
    },
  });
}
